import React, { useEffect } from 'react';
import { message } from 'antd';
import { GoogleMapsProvider } from '../../components/GoogleMapsProvider';
import ShipmentDetailDrawerWithTeams from './ShipmentDetailDrawerWithTeams';
import EditShipmentDrawerWithTeams from './EditShipmentDrawerWithTeams';
import ShipmentNotificationDrawer from './ShipmentNotifications/ShipmentNotificationDrawer';
import { Transcripts } from './AiTranscripts/Transcripts';
import ShipmentProductsListDrawer from './ShipmentProductsListDrawer';
import ShipmentLegsListDrawer from './ShipmentLegsListDrawer';
import PickupDetailsDrawer from './components/PickupDetailsDrawer';
import SchedulePickupDrawer from './components/SchedulePickupDrawer';
import ExecutionLegDetailDrawer from './ExecutionLegDetailDrawer';
import type { CocEmail, SingleShipmentDataExtended } from '../../apis/types';
import type { ShipmentActivityBell } from './ControlTower/types';
import type { CocCommentCreatedMeta } from './partials/ReplyCommentBox';

export interface DrawerAlert {
  id?: string;
  isFromCalendarMore?: boolean;
  isFromHome?: boolean;
  isFromActivityFeed?: boolean; //Users Activity, separate "User activity" back-arrow
  CoC_ID?: string;
  /** Shipment notification to highlight in the notifications drawer. */
  notificationId?: string;
  scrollToCommentsBottom?: boolean;
  initialAction?: string;
  /**
   * The email panel (emailTranscripts) is opened with the CoC entry that was
   * clicked: its per-email fields, and whether Reply (not View) was clicked.
   */
  email?: CocEmail;
  open_composer?: boolean;
}

export interface DrawerState {
  isOpen: boolean;
  name: string;
  shipmentId?: string | null;
  alert?: DrawerAlert | null;
  legType?: 'CL' | 'EL';
}
interface ShipmentDrawerProps {
  fromTable?: boolean;
  allPermissions: Record<string, unknown>; // TODO(tim): type this!
  drawerState: DrawerState;
  toggleDrawer: (
    state: 'open' | 'close',
    name: string,
    shipmentId: string | null,
    alert?: unknown,
    legType?: 'CL' | 'EL'
  ) => void;
  shipmentSuccess: () => void;
  filterByLinkCode: (linkCode: string) => void;
  onEditShipmentSuccess: (data: unknown) => void;
  onLinkedShipmentClick?: (shipmentId: string) => void;
  /** Quote-from-shell handler; only fires when the detail loads a shell shipment
   *  and the user clicks the Quote action in the header. */
  onQuoteFromShipment?: (shipment: SingleShipmentDataExtended) => void;
  /** Book-from-shell handler; only fires when the detail loads a shell shipment
   *  and the user clicks the Book action in the header. */
  onBookFromShipment?: (shipment: SingleShipmentDataExtended) => void;
  /** FedEx return label from the detail … menu. */
  onGenerateReturnLabel?: (shipment: SingleShipmentDataExtended) => void;
  onCocCommentCreated?: (
    shipmentId: string,
    comment: CocCommentCreatedMeta
  ) => void;
  onShipmentCommentsRead?: (shipmentId: string) => void;
  onShipmentNotificationActivityChange?: (
    shipmentId: string,
    bell: ShipmentActivityBell
  ) => void;
  /** Incremented by the parent after an external action (e.g. FedEx promote)
   *  that should cause the detail view to refetch. */
  detailRefreshKey?: number;
  legType?: 'CL' | 'EL';
}

const ShipmentDrawer: React.FC<ShipmentDrawerProps> = ({
  fromTable = false,
  allPermissions,
  drawerState,
  toggleDrawer,
  shipmentSuccess,
  filterByLinkCode,
  onEditShipmentSuccess,
  onLinkedShipmentClick,
  onQuoteFromShipment,
  onBookFromShipment,
  onGenerateReturnLabel,
  onCocCommentCreated,
  onShipmentCommentsRead,
  onShipmentNotificationActivityChange,
  detailRefreshKey,
  legType,
}) => {
  const { isOpen, name, shipmentId } = drawerState;

  // Back to Detail from the notifications drawer keeps the "User activity"
  // link when it was opened from the activity feed.
  const notificationsToggleDrawer: ShipmentDrawerProps['toggleDrawer'] = (
    state,
    nextName,
    id,
    alert,
    nextLegType
  ) =>
    toggleDrawer(
      state,
      nextName,
      id,
      alert ??
        (state === 'open' &&
        nextName === 'Detail' &&
        drawerState.alert?.isFromActivityFeed
          ? { isFromActivityFeed: true }
          : undefined),
      nextLegType
    );

  useEffect(() => {
    if (isOpen) {
      document.documentElement.classList.add('hide-scrollbar');
    } else {
      document.documentElement.classList.remove('hide-scrollbar');
    }
  }, [isOpen]);

  return (
    <>
      <div
        className={`custom-card-drawer ${isOpen ? 'open' : ''} ${
          fromTable ? 'from-table' : ''
        } ${name === 'SchedulePickup' ? 'pickup-scroll' : ''}`}
      >
        {name === 'Detail' ? (
          <GoogleMapsProvider>
            <ShipmentDetailDrawerWithTeams
              drawerAlert={drawerState.alert}
              isOpen={isOpen}
              openShipmentId={shipmentId}
              toggleDrawer={toggleDrawer}
              shipmentSuccess={shipmentSuccess}
              filterByLinkCode={filterByLinkCode}
              onLinkedShipmentClick={onLinkedShipmentClick}
              isFromHome={Boolean(drawerState.alert?.isFromHome)}
              isFromActivityFeed={Boolean(
                drawerState.alert?.isFromActivityFeed
              )}
              onQuoteFromShipment={onQuoteFromShipment}
              onBookFromShipment={onBookFromShipment}
              onGenerateReturnLabel={onGenerateReturnLabel}
              supportsSchedulePickup
              onCocCommentCreated={onCocCommentCreated}
              onShipmentCommentsRead={onShipmentCommentsRead}
              externalRefreshKey={detailRefreshKey}
              legType={legType}
            />
          </GoogleMapsProvider>
        ) : null}

        {name === 'Edit' ? (
          <EditShipmentDrawerWithTeams
            shipmentId={shipmentId}
            toggleDrawer={(value: boolean, name = 'Edit') => {
              toggleDrawer(
                value ? 'open' : 'close',
                name,
                shipmentId ?? null,
                undefined,
                value ? legType : undefined
              );
            }}
            onEditShipmentSuccess={onEditShipmentSuccess}
          />
        ) : null}
        {name === 'shipmentProductsList' ? (
          <ShipmentProductsListDrawer
            toggleDrawer={(value: boolean, name = 'shipmentProductsList') => {
              toggleDrawer(
                value ? 'open' : 'close',
                name,
                shipmentId ?? null,
                undefined,
                value ? legType : undefined
              );
            }}
            shipmentId={shipmentId}
            legType={legType}
          />
        ) : null}
        {name === 'shipmentLegsList' ? (
          <ShipmentLegsListDrawer
            toggleDrawer={(value: boolean, name = 'shipmentLegsList') => {
              toggleDrawer(
                value ? 'open' : 'close',
                name,
                shipmentId ?? null,
                undefined,
                value ? (legType === 'CL' ? 'EL' : 'CL') : undefined
              );
            }}
            shipmentId={shipmentId}
            initialLegType={legType ?? 'CL'}
            onLegClick={(leg) => {
              const targetId = leg.id;
              if (!targetId) {
                message.error(
                  leg.leg_type === 'EL'
                    ? 'Leg ID is not available.'
                    : 'Shipment ID is not available for this leg.'
                );
                return;
              }

              toggleDrawer('open', 'Detail', targetId, undefined, leg.leg_type);
            }}
          />
        ) : null}

        {name === 'SchedulePickup' && shipmentId ? (
          <SchedulePickupDrawer
            shipmentId={shipmentId}
            legType={legType}
            toggleDrawer={toggleDrawer}
          />
        ) : null}
        {name === 'PickupDetails' && shipmentId ? (
          <PickupDetailsDrawer
            shipmentId={shipmentId}
            legType={legType}
            toggleDrawer={toggleDrawer}
          />
        ) : null}
        {name === 'notifications' ? (
          <ShipmentNotificationDrawer
            allPermissions={allPermissions}
            shipmentId={shipmentId}
            highlightNotificationId={drawerState.alert?.notificationId}
            toggleDrawer={notificationsToggleDrawer}
            onShipmentNotificationActivityChange={
              onShipmentNotificationActivityChange
            }
          />
        ) : null}
        {name === 'emailTranscripts' || name === 'callTranscripts' ? (
          <Transcripts
            shipmentId={shipmentId}
            drawerState={drawerState}
            toggleDrawer={toggleDrawer}
          />
        ) : null}
        {name === 'ELDetail' ? (
          <ExecutionLegDetailDrawer
            elId={shipmentId}
            toggleDrawer={toggleDrawer}
          />
        ) : null}
      </div>
    </>
  );
};

export default ShipmentDrawer;
